import React, { useState, useEffect, useCallback } from 'react';

const TelegramPromoModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Track scroll-downs and show after 4 significant scroll-downs
  useEffect(() => {
    // Don't show if already shown this session
    if (sessionStorage.getItem('tg_promo_shown')) return;

    let scrollCount = 0;
    let lastScrollY = window.scrollY;
    let scrollAccumulator = 0;
    const SCROLL_THRESHOLD = window.innerHeight * 0.8; // ~1 viewport per "scroll"
    const REQUIRED_SCROLLS = 4;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY;

      if (delta > 0) {
        // Scrolling down
        scrollAccumulator += delta;
        if (scrollAccumulator >= SCROLL_THRESHOLD) {
          scrollCount++;
          scrollAccumulator = 0;
          if (scrollCount >= REQUIRED_SCROLLS) {
            setIsOpen(true);
            sessionStorage.setItem('tg_promo_shown', '1');
            window.removeEventListener('scroll', handleScroll);
          }
        }
      } else {
        scrollAccumulator = 0;
      }

      lastScrollY = currentY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 300);
  }, []);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes tgPromoFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes tgPromoFadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes tgPromoSlideUp {
          from { transform: translateY(40px) scale(0.95); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes tgPromoSlideDown {
          from { transform: translateY(0) scale(1); opacity: 1; }
          to { transform: translateY(40px) scale(0.95); opacity: 0; }
        }
        @keyframes tgGlowPulse {
          0%, 100% {
            box-shadow:
              0 0 20px rgba(139, 92, 246, 0.4),
              0 0 40px rgba(236, 72, 153, 0.2),
              0 0 60px rgba(139, 92, 246, 0.1);
          }
          50% {
            box-shadow:
              0 0 30px rgba(139, 92, 246, 0.6),
              0 0 60px rgba(236, 72, 153, 0.3),
              0 0 80px rgba(139, 92, 246, 0.2);
          }
        }
        @keyframes tgShimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        .tg-promo-overlay {
          animation: tgPromoFadeIn 0.3s ease forwards;
        }
        .tg-promo-overlay.closing {
          animation: tgPromoFadeOut 0.3s ease forwards;
        }
        .tg-promo-card {
          animation: tgPromoSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .tg-promo-overlay.closing .tg-promo-card {
          animation: tgPromoSlideDown 0.3s ease forwards;
        }

        .tg-promo-btn {
          animation: tgGlowPulse 2s ease-in-out infinite;
          transition: transform 0.2s ease, filter 0.2s ease;
        }
        .tg-promo-btn:hover {
          transform: translateY(-3px) scale(1.03);
          filter: brightness(1.15);
        }
        .tg-promo-btn:active {
          transform: translateY(0) scale(0.98);
        }

        .tg-promo-close:hover {
          background: rgba(255, 255, 255, 0.15) !important;
          transform: rotate(90deg);
        }

        @media (max-width: 768px) {
          .tg-promo-card {
            margin: 16px !important;
            max-width: calc(100vw - 32px) !important;
            border-radius: 20px !important;
          }
          .tg-promo-image {
            height: 180px !important;
          }
          .tg-promo-title {
            font-size: 28px !important;
          }
          .tg-promo-text {
            font-size: 15px !important;
            padding: 0 16px !important;
          }
          .tg-promo-body {
            padding: 24px 20px 28px !important;
          }
        }
        @media (max-width: 480px) {
          .tg-promo-image {
            height: 150px !important;
          }
          .tg-promo-title {
            font-size: 24px !important;
          }
          .tg-promo-text {
            font-size: 14px !important;
          }
        }
      `}</style>

      <div
        className={`tg-promo-overlay ${isClosing ? 'closing' : ''}`}
        onClick={handleClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(10, 5, 30, 0.75)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px',
        }}
      >
        <div
          className="tg-promo-card"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(180deg, #1A1030 0%, #0F0A1F 100%)',
            borderRadius: '28px',
            maxWidth: '480px',
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.5), 0 0 40px rgba(139, 92, 246, 0.1)',
          }}
        >
          {/* Close button */}
          <button
            className="tg-promo-close"
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: 'rgba(255, 255, 255, 0.7)',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              transition: 'all 0.3s ease',
              zIndex: 10,
              backdropFilter: 'blur(4px)',
            }}
          >
            ×
          </button>

          {/* Image */}
          <div
            className="tg-promo-image"
            style={{
              width: '100%',
              height: '220px',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <img
              src="/images/shapka.webp"
              alt=""
              loading="eager"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
              }}
            />
            {/* Gradient overlay at bottom of image */}
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '60px',
              background: 'linear-gradient(transparent, #1A1030)',
            }} />
          </div>

          {/* Body */}
          <div
            className="tg-promo-body"
            style={{
              padding: '20px 32px 36px',
              textAlign: 'center',
            }}
          >
            {/* Title */}
            <h2
              className="tg-promo-title"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '36px',
                fontWeight: 900,
                color: '#FFFFFF',
                margin: '0 0 16px 0',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}
            >
              Опаааньки!
            </h2>

            {/* Text */}
            <p
              className="tg-promo-text"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '16px',
                fontWeight: 400,
                color: 'rgba(255, 255, 255, 0.75)',
                margin: '0 0 28px 0',
                lineHeight: 1.6,
                padding: '0 8px',
              }}
            >
              Рассказали все все секреты прибыльного продвижения в телеграм канале директора Агентства Первый Селлер!
            </p>

            {/* CTA Button with GLOW */}
            <a
              href="https://t.me/+mCCHksrjcSQ1ZjYy"
              target="_blank"
              rel="noopener noreferrer"
              className="tg-promo-btn"
              style={{
                display: 'inline-block',
                padding: '16px 48px',
                borderRadius: '60px',
                background: 'linear-gradient(135deg, #7DD3FC 0%, #A78BFA 35%, #F472B6 65%, #FCD34D 100%)',
                backgroundSize: '200% auto',
                color: '#FFFFFF',
                fontFamily: "'Inter', sans-serif",
                fontSize: '18px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                textDecoration: 'none',
                cursor: 'pointer',
                border: 'none',
              }}
            >
              Смотреть
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default TelegramPromoModal;
